import React from 'react';
import {useMutation} from '@apollo/client/react';
import {PersonAdd} from '@mui/icons-material';
import {Button} from '@mui/material';
import ButtonDialog from './ButtonDialog';
import {useListContext} from 'components/list/ListContext';
import {useDialogContext} from 'components/dialog/DialogContext';
import QUERIES from 'services/gqlQueries';
import {toast} from 'react-toastify';
import {DeviceRecord, isUserUidDevice} from './BulkMergeDevices';

export const getMapCompetitorsToUserValidation = (selectedIds: Array<string | number>, list: DeviceRecord[]) => {
  if (selectedIds.length === 0) {
    return 'Select at least one device.';
  }

  const devices = list.filter((device) => selectedIds.includes(device.id));
  const invalidDevices = devices.filter((device) => !isUserUidDevice(device));

  if (invalidDevices.length > 0) {
    return `Only devices linked to a user (imei matches user uid) can be mapped: ${invalidDevices.map((device) => device.name).join(', ')}`;
  }

  return null;
};

const BulkMapCompetitorsToUserConfirm = ({
  onSuccess,
  validationError
}: {
  onSuccess: () => void;
  validationError: string | null;
}) => {
  const {close} = useDialogContext();
  const {selected: ids} = useListContext();
  const [mutate, {loading}] = useMutation<{item: {updatedCount: number}}>(QUERIES.mapCompetitorsToDeviceUserAllDevice);

  const handleConfirm = () => {
    if (validationError) {
      return;
    }

    mutate({variables: {ids}})
      .then((res) => {
        const updatedCount = res.data?.item?.updatedCount ?? 0;
        toast.success(`Mapped user to ${updatedCount} competitor${updatedCount === 1 ? '' : 's'}.`);
        close();
        onSuccess();
      })
      .catch((error: Error) => {
        if (error.message) {
          toast.error(error.message);
        }
      });
  };

  return (
    <Button variant="contained" onClick={handleConfirm} disabled={loading || !!validationError}>
      {loading ? 'Loading...' : 'Confirm'}
    </Button>
  );
};

const BulkMapCompetitorsToUser = () => {
  const {refetch, setSelected, selected, list} = useListContext();
  const validationError = getMapCompetitorsToUserValidation(selected, list);

  const onSuccess = () => {
    setSelected([]);
    if (typeof refetch === 'function') {
      refetch();
    }
  };

  const selectedDevices = list.filter((device) => selected.includes(device.id));

  return (
    <ButtonDialog
      buttonLabel="Map competitors to user"
      variant="text"
      maxWidth="sm"
      startIcon={<PersonAdd />}
      dialogTitle="Map competitors to user"
      primaryButton={<BulkMapCompetitorsToUserConfirm onSuccess={onSuccess} validationError={validationError} />}
    >
      {validationError ? (
        validationError
      ) : (
        <>
          Assign the linked user to all competitors without a user on{' '}
          {selectedDevices.length === 1 ? (
            <>
              device <strong>{selectedDevices[0]?.name}</strong>
            </>
          ) : (
            <>
              <strong>{selectedDevices.length}</strong> devices ({selectedDevices.map((device) => device.name).join(', ')})
            </>
          )}
          ?
        </>
      )}
    </ButtonDialog>
  );
};

export default BulkMapCompetitorsToUser;
